<template>
  <div>
    <div class="content-header" v-if="!embedded">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6">
            <h1 class="m-0">{{ this.$route.name }}</h1>
          </div>
          <div class="col-sm-6">
            <ol class="breadcrumb float-sm-right">
              <li class="breadcrumb-item active"><i v-bind:class="routeIcon"></i> {{ $t(this.$route.name) }}</li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <b-modal
        ref="editFormModal"
        size="lg"
        no-close-on-backdrop
        no-close-on-esc
        :title="editModalTitle"
        :static="true"
    >
      <edit-form ref="editForm"></edit-form>

      <template #modal-footer>
        <b-row class="mt-3">
          <b-col>
            <button type="button" class="btn mr-1 btn-primary float-right" v-on:click="saveItem">Save</button>
          </b-col>
        </b-row>
      </template>
    </b-modal>

    <section class="content">
      <div class="container-fluid">
        <b-row class="col-lg-12">
          <b-form-group class="col-lg-12 col-md-12 col-sm-6">
            <button type="button" class="btn mr-1 btn-success" v-on:click="showCreateModal"><i class="fas fa-plus"></i> Create</button>
          </b-form-group>
        </b-row>

        <div class="row col-12">
          <div class="col-12 mb-10">
            <div class="table-wrapper mb-10">
              <table class="table table-sm opp-table">
                <thead>
                  <tr>
                    <th>GameType</th>
                    <th>GameTypeName</th>
                    <th class="sortable" @click="toggleSidebetSort">
                      Sidebets
                      <i class="fas" :class="sidebetSortDesc ? 'fa-sort-down' : 'fa-sort-up'"></i>
                    </th>
                    <th>Opposite Sidebets</th>
                    <th>Enabled</th>
                    <th style="width: 260px;">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(group, groupIndex) in groupedItems">
                    <tr
                        v-for="(item, rowIndex) in group.rows"
                        :key="item.id"
                        :class="[groupIndex % 2 === 1 ? 'group-odd' : '', {'group-hover': hoveredGroup === groupIndex}]"
                        @mouseenter="hoveredGroup = groupIndex"
                        @mouseleave="hoveredGroup = null"
                    >
                      <td v-if="rowIndex === 0" :rowspan="group.rows.length" class="align-middle font-weight-bold">
                        {{ group.gameType }}
                      </td>
                      <td v-if="rowIndex === 0" :rowspan="group.rows.length" class="align-middle">
                        {{ group.gameTypeName }}
                      </td>
                      <td>{{ item.sideBetType }}</td>
                      <td>{{ (item.oppositeSideBetTypes || []).join(', ') }}</td>
                      <td>
                        <b-badge :variant="item.enabled ? 'success' : 'secondary'">
                          {{ item.enabled ? 'Enabled' : 'Disabled' }}
                        </b-badge>
                      </td>
                      <td v-if="rowIndex === 0" :rowspan="group.rows.length" class="align-middle">
                        <button type="button" class="btn mr-1 btn-primary btn-xs" v-on:click="showManageModal(group)">
                          <i class="fas fa-edit"></i> Edit
                        </button>
                        <RemoveForm
                            :object="removeGroupLabel(group)"
                            message="Please confirm removing all opposite side bets for this game type"
                            :url="'/api/v1/baccarat-sidebet-opposites/game-type/' + group.gameType"
                            @finish="loadItems"
                        />
                      </td>
                    </tr>
                  </template>
                  <tr v-if="!groupedItems.length">
                    <td colspan="6" class="text-center">{{ emptyText }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from "axios";
import {getRouteIcon, ROUTE_LIMITS} from "../../utils/routerIcons";
import EditForm from "../../components/BaccaratSidebetOpposite/EditForm";
import RemoveForm from "../../components/remove-form/RemoveForm.vue";

export default {
  components: {
    EditForm,
    RemoveForm
  },
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      editModalTitle: null,
      cancel: null,
      error: null,
      items: [],
      sidebetSortDesc: false,
      hoveredGroup: null,
    }
  },
  computed: {
    emptyText() {
      if (this.cancel) {
        return 'Loading items..';
      }
      if (this.error) {
        return this.error.toString();
      }
      return 'There are no records to show';
    },
    routeIcon: () => getRouteIcon(ROUTE_LIMITS),
    groupedItems() {
      const groups = {};
      const order = [];

      for (const item of this.items) {
        if (!groups[item.gameType]) {
          groups[item.gameType] = {
            gameType: item.gameType,
            gameTypeName: this.$store.getters['gameTypes/getName'](item.gameType),
            rows: []
          };
          order.push(item.gameType);
        }
        groups[item.gameType].rows.push(item);
      }

      const dir = this.sidebetSortDesc ? -1 : 1;
      order.forEach(gameType => {
        groups[gameType].rows.sort((a, b) => (a.sideBetType - b.sideBetType) * dir);
      });

      // primary sort by gameType ascending
      order.sort((a, b) => a - b);

      return order.map(gameType => groups[gameType]);
    }
  },
  methods: {
    removeGroupLabel(group) {
      return 'GameType ' + group.gameType + ' (' + group.rows.length + ' opposite' + (group.rows.length === 1 ? '' : 's') + ')';
    },
    toggleSidebetSort() {
      this.sidebetSortDesc = !this.sidebetSortDesc;
    },
    showCreateModal() {
      this.editModalTitle = 'Create Opposite Bets';
      this.$refs.editForm.setData({});
      this.$refs.editFormModal.show();
    },
    showManageModal(group) {
      this.editModalTitle = 'Edit Opposite Bets — GameType ' + group.gameType;
      this.$refs.editForm.setData({gameType: group.gameType, records: group.rows});
      this.$refs.editFormModal.show();
    },
    saveItem() {
      this.$refs.editForm.isValid().then(result => {
        if (!result) {
          return;
        }

        const data = this.$refs.editForm.getData();

        this
            .$http({
              url: data.isManage
                  ? '/api/v1/baccarat-sidebet-opposites/game-type/' + data.gameType
                  : '/api/v1/baccarat-sidebet-opposites',
              data: data.isManage
                  ? {items: data.items}
                  : {gameType: data.gameType, items: data.items},
              method: data.isManage ? 'PUT' : 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
            })
            .then(() => {
              setTimeout(() => {
                this.$refs.editFormModal.hide();
              }, 300);

              this.$toastr.i('Success');
              this.loadItems();
            })
            .catch((err) => {
              this.$toastr.e(JSON.stringify(err.response.data.message));
              throw err;
            });
      });
    },
    loadItems() {
      let self = this;
      this.error = null;

      this
          .$http({
            url: '/api/v1/baccarat-sidebet-opposites',
            method: 'GET',
            cancelToken: new axios.CancelToken(c => { self.cancel = c; })
          })
          .then(resp => {
            this.items = resp.data;
          })
          .catch((err) => {
            if (!axios.isCancel(err)) {
              this.items = [];
              this.error = err;
            }
          })
          .finally(() => {
            this.cancel = null;
          });
    },
  },
  mounted() {
    this.$store.dispatch('gameTypes/loadList');
    this.loadItems();
  }
}
</script>

<style scoped>
th.sortable {
  cursor: pointer;
  user-select: none;
}

.opp-table tbody tr.group-odd > td {
  background-color: rgba(0, 0, 0, 0.05);
}

.opp-table tbody tr.group-hover > td {
  background-color: rgba(0, 0, 0, 0.075);
}

.opp-table td {
  vertical-align: middle;
}
</style>
